import React, { useMemo } from 'react';
import Icon from '../AppIcon';
import { formatDateForDB, isToday, normalizeDateFromDB, formatTimeWithoutSeconds, calculateEndTime } from '../../utils/dateUtils';

const MonthlyCalendarGrid = ({ 
  appointments = [],
  onDateClick,
  onAppointmentClick,
  selectedDate = new Date()
}) => {
  const currentMonth = useMemo(() => {
    const date = new Date(selectedDate);
    date?.setDate(1);
    date?.setHours(0, 0, 0, 0);
    return date;
  }, [selectedDate]);

  const monthDays = useMemo(() => {
    const year = currentMonth?.getFullYear();
    const month = currentMonth?.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const firstDayOfWeek = firstDay?.getDay();
    const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    
    const days = [];
    
    for (let i = 0; i < startOffset; i++) {
      const prevDate = new Date(year, month, -startOffset + i + 1);
      days?.push({
        date: prevDate,
        dateString: formatDateForDB(prevDate),
        dayNumber: prevDate?.getDate(),
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    for (let day = 1; day <= lastDay?.getDate(); day++) {
      const date = new Date(year, month, day);
      days?.push({
        date: date,
        dateString: formatDateForDB(date),
        dayNumber: day,
        isCurrentMonth: true,
        isToday: isToday(date)
      });
    }
    
    const remainingDays = 42 - days?.length;
    for (let i = 1; i <= remainingDays; i++) {
      const nextDate = new Date(year, month + 1, i);
      days?.push({
        date: nextDate,
        dateString: formatDateForDB(nextDate),
        dayNumber: nextDate?.getDate(),
        isCurrentMonth: false,
        isToday: false
      });
    }
    
    return days;
  }, [currentMonth]);

  const getAppointmentsForDate = (dateString) => {
    return appointments?.filter(apt => {
      const aptDate = normalizeDateFromDB(apt?.date);
      return aptDate === dateString;
    });
  };

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <div className="flex flex-col h-full">
      <div className="grid grid-cols-7 gap-px bg-border border-b border-border">
        {dayNames?.map((dayName) => (
          <div 
            key={dayName}
            className="bg-card px-4 py-3 text-center"
          >
            <span className="text-sm font-caption font-medium text-muted-foreground">
              {dayName}
            </span>
          </div>
        ))}
      </div>
      <div className="flex-1 grid grid-cols-7 gap-px bg-border">
        {monthDays?.map((day, index) => {
          const dayAppointments = getAppointmentsForDate(day?.dateString);
          const hasAppointments = dayAppointments?.length > 0;
          
          return (
            <button
              key={`${day?.dateString}-${index}`}
              onClick={() => {
                if (hasAppointments) {
                  onAppointmentClick(dayAppointments?.[0]);
                } else {
                  onDateClick(day?.dateString);
                }
              }}
              className={`bg-card p-2 min-h-[100px] md:min-h-[120px] text-left transition-smooth hover:bg-muted relative ${
                day?.isToday ? 'bg-primary/5 ring-2 ring-primary/20 ring-inset' : ''
              } ${
                !day?.isCurrentMonth ? 'opacity-40' : ''
              }`}
            >
              <div className={`text-sm font-data mb-2 ${
                day?.isToday ? 'text-primary font-semibold' : day?.isCurrentMonth ? 'text-foreground' : 'text-muted-foreground'
              }`}>
                {day?.dayNumber}
              </div>
              {hasAppointments && (
                <div className="space-y-1">
                  {dayAppointments?.slice(0, 3)?.map((apt) => (
                    <div
                      key={apt?.id}
                      className="px-2 py-1 bg-accent/10 border border-accent/20 rounded text-xs font-medium text-accent hover:bg-accent/20 transition-smooth truncate"
                      onClick={(e) => {
                        e?.stopPropagation();
                        onAppointmentClick(apt);
                      }}
                    >
                      <div className="flex items-center gap-1">
                        <Icon name="Clock" size={10} />
                        <span className="font-data">
                          {formatTimeWithoutSeconds(apt?.time)} - {calculateEndTime(apt?.time, apt?.duration)}
                        </span>
                      </div>
                      <div className="truncate text-[10px] mt-0.5">
                        {apt?.customerName || apt?.customer_name}
                      </div>
                      <div className="truncate text-[10px] text-accent/70">
                        {apt?.services?.name || apt?.services?.description || apt?.service || 'No service'}
                      </div>
                    </div>
                  ))}
                  {dayAppointments?.length > 3 && (
                    <div className="text-[10px] text-muted-foreground font-medium px-2">
                      +{dayAppointments?.length - 3} more
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MonthlyCalendarGrid;